import React, { useState } from 'react';
import { MessageSquare, Send, ChevronDown, ChevronUp } from 'lucide-react';
import { ref, set } from 'firebase/database';
import { database } from '../../firebase/config';
import { trackFeedbackSubmitted } from '../../firebase-analytics';

export default function ContactFeedback({ user, bandId, bandData, setError, showMessage }) {
  const [expanded, setExpanded] = useState(false);
  const [feedbackType, setFeedbackType] = useState('bug');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [sending, setSending] = useState(false);

  const sendFeedback = async () => {
    if (!feedbackMessage.trim()) {
      setError('Please enter a message');
      return;
    }
    try {
      setSending(true);
      setError('');

      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
      const randomSuffix = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      const feedbackId = `FB-${dateStr}-${randomSuffix}`;

      const feedbackRef = ref(database, `feedback/${feedbackId}`);
      await set(feedbackRef, {
        oderId: feedbackId,
        userEmail: user.email,
        userName: bandData?.members?.[user.uid]?.displayName || user.displayName || user.email,
        bandId: bandId,
        bandName: bandData?.name || 'Unknown',
        bandInviteCode: bandData?.inviteCode || 'Unknown',
        type: feedbackType,
        message: feedbackMessage.trim(),
        timestamp: new Date().toISOString(),
        status: 'new'
      });

      trackFeedbackSubmitted(feedbackType);
      setFeedbackMessage('');
      setFeedbackType('bug');
      showMessage('Feedback sent successfully! Thank you for helping improve Tapsody.');
    } catch (error) {
      setError('Failed to send feedback: ' + error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card-3d rounded-card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left"
      >
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-white" />
          Contact & Feedback
        </h2>
        {expanded ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
      </button>

      {expanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6">
          <p className="text-sm text-text-subtle mb-4">
            Have a bug report, feature suggestion, or question? We'd love to hear from you!
          </p>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-text-subtle mb-2 block">Type</label>
              <select
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
                disabled={sending}
                className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-white focus:outline-none focus:border-brand rounded-pill"
              >
                <option value="bug">Report a Bug</option>
                <option value="suggestion">Feature Suggestion</option>
                <option value="question">Question</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-text-subtle mb-2 block">Message</label>
              <textarea
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                disabled={sending}
                placeholder="Describe your issue or suggestion in detail..."
                className="w-full bg-transparent border border-surface-border-light px-4 py-3 text-white placeholder-text-subtle focus:outline-none focus:border-brand resize-none rounded-card"
                rows="5"
              />
              <p className="text-xs text-text-subtle mt-1">
                We'll review your message and respond within 24 hours. During busy times, our response time may be longer.
              </p>
            </div>

            <button
              onClick={sendFeedback}
              disabled={sending || !feedbackMessage.trim()}
              className="w-full bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition h-btn font-semibold flex items-center justify-center gap-2 rounded-pill"
            >
              <Send className="w-4 h-4" />
              {sending ? 'Sending...' : 'Send Feedback'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
